import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type {
  CampaignInterface,
  CampaignLaunchReadinessResult,
  CampaignMessageInterface,
  CampaignMessageParams,
  CampaignMetricsInterface,
  CampaignMetricsParams,
  CampaignRecipientRowInterface,
  CampaignRecipientsParams
} from '@/interfaces/Campaign.interface';
import { useApiClient } from '../context';
import type { ListDataResponse } from '../types';
import { shallowCopyListData } from '../shallow-copy-list-data';
import {
  campaignsQueryKeys,
  type CampaignsListQueryParams,
  type CampaignRecipientsQueryParams
} from './query-keys';

type CampaignsListData = ListDataResponse<CampaignInterface>;

export function useCampaignsListQuery(
  params: CampaignsListQueryParams,
  options?: Omit<
    UseQueryOptions<CampaignsListData>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    ...options,
    queryKey: campaignsQueryKeys.list(params),
    queryFn: () => api.campaigns.list(shallowCopyListData(params.listData))
  });
}

export function useCampaignQuery(
  uuid: string | undefined,
  options?: Omit<UseQueryOptions<CampaignInterface>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      uuid != null
        ? campaignsQueryKeys.detail(uuid)
        : [...campaignsQueryKeys.details(), 'disabled'],
    queryFn: () => {
      if (uuid == null) throw new Error('Campaign uuid is required');
      return api.campaigns.show(uuid);
    },
    enabled: (enabledOption ?? true) && uuid != null
  });
}

export function useCampaignLaunchReadinessQuery(
  uuid: string | undefined,
  options?: Omit<
    UseQueryOptions<CampaignLaunchReadinessResult>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      uuid != null
        ? campaignsQueryKeys.launchReadiness(uuid)
        : [...campaignsQueryKeys.all, 'launch-readiness', 'disabled'],
    queryFn: () => {
      if (uuid == null) throw new Error('Campaign uuid is required');
      return api.campaigns.getLaunchReadiness(uuid);
    },
    enabled: (enabledOption ?? true) && uuid != null
  });
}

export function useCampaignMetricsQuery(
  uuid: string | undefined,
  params?: CampaignMetricsParams,
  options?: Omit<
    UseQueryOptions<CampaignMetricsInterface>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery({
    ...rest,
    queryKey:
      uuid != null
        ? campaignsQueryKeys.metrics({ uuid, ...params })
        : [...campaignsQueryKeys.all, 'metrics', 'disabled'],
    queryFn: () => {
      if (uuid == null) throw new Error('Campaign uuid is required');
      return api.campaigns.getMetrics(uuid, params);
    },
    enabled: (enabledOption ?? true) && uuid != null
  });
}

export function useCampaignMessageQuery(
  uuid: string | undefined,
  params: CampaignMessageParams,
  options?: Omit<
    UseQueryOptions<CampaignMessageInterface>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  const hasRecipient = params.recipient.trim() !== '';
  return useQuery({
    ...rest,
    queryKey:
      uuid != null && hasRecipient
        ? campaignsQueryKeys.message({ uuid, ...params })
        : [...campaignsQueryKeys.all, 'message', 'disabled'],
    queryFn: () => {
      if (uuid == null) throw new Error('Campaign uuid is required');
      return api.campaigns.getMessage(uuid, params);
    },
    enabled: (enabledOption ?? true) && uuid != null && hasRecipient
  });
}

type CampaignRecipientsData = ListDataResponse<CampaignRecipientRowInterface>;

export function useCampaignRecipientsQuery(
  uuid: string | undefined,
  params: Omit<CampaignRecipientsQueryParams, 'uuid'>,
  options?: Omit<
    UseQueryOptions<CampaignRecipientsData>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  const { enabled: enabledOption, ...rest } = options ?? {};
  const queryParams: CampaignRecipientsQueryParams = {
    uuid: uuid ?? '',
    ...params
  };

  return useQuery({
    ...rest,
    queryKey:
      uuid != null
        ? campaignsQueryKeys.recipients(queryParams)
        : [...campaignsQueryKeys.all, 'recipients', 'disabled'],
    queryFn: () => {
      if (uuid == null) throw new Error('Campaign uuid is required');
      return api.campaigns.getRecipients(
        uuid,
        shallowCopyListData(params.listData),
        params.searchTerm,
        {
          scope: params.scope,
          followUpWorkflowId: params.followUpWorkflowId
        }
      );
    },
    enabled: (enabledOption ?? true) && uuid != null
  });
}
